import { useEffect, useState } from 'react';
import { api } from './api';
import { startClockPoller, type TeamClockView } from './team-clock';

/**
 * The PunchWISE clock status for one facility's On-site team, refreshed every
 * 60 s while the browser tab is visible (see `startClockPoller`).
 *
 * `enabled` is false when the panel is not on screen — another tab of the
 * facility page, or a team that is not present — and then nothing is asked and
 * the answer is DROPPED, so returning to the panel never shows a status from
 * minutes ago while the fresh one loads.
 *
 * The answer is held against the facility it was asked for: a store's clock
 * status can never be shown on the next store's page while that page's own
 * answer is on its way.
 */
export function useTeamClock(facilityId: string | null, enabled: boolean): TeamClockView | null {
  const [held, setHeld] = useState<{ id: string; view: TeamClockView } | null>(null);

  useEffect(() => {
    setHeld(null);
    if (!facilityId || !enabled) return;
    return startClockPoller({
      load: () => api.teamClock(facilityId),
      onAnswer: (view) => setHeld({ id: facilityId, view }),
      doc: document,
    });
  }, [facilityId, enabled]);

  return enabled && held && held.id === facilityId ? held.view : null;
}
